import type {
  ClientChatMessage,
  ClientCommandMessage,
  ClientHorizonMessage,
  ClientKeyMessage,
  ClientLoginMessage,
  ClientMessage,
  ClientPongMessage,
  ClientSayMessage,
  ClientScoreDetailedMessage,
  ClientTeamChatMessage,
  ClientVoteMuteMessage,
  ClientWhisperMessage,
  SERVER_ERRORS,
  ServerCustomBTRData,
  ServerCustomCTFData,
  ServerCustomSwitchGameData,
  ServerEventLeaveHorizonMessage,
  ServerEventRepelMessage,
  ServerLoginMessage,
  ServerMessage,
  ServerPacketCode,
  ServerPlayerFireMessage,
  ServerPlayerHitMessage,
  ServerServerCustomMessage,
} from '@airmash/protocol';
import {
  CLIENT_PACKETS,
  KEY_CODES,
  LEAVE_HORIZON_TYPES,
  MOB_TYPES,
  SERVER_CUSTOM_TYPES,
  SERVER_MESSAGE_TYPES,
  SERVER_PACKETS,
} from '@airmash/protocol';
import { toServerFlag, UNKNOWN_FLAG } from '../services/Flags';
import { uiState } from '../ui/UIState';
import { handleFirewall as btrHandleFirewall, playersAlive as btrPlayersAlive, showWin as btrShowWin } from './Btr';
import { clock } from './Clock';
import { EXTENSION_LEVEL, MS_PER_FRAME_60FPS, PROTOCOL_VERSION } from './Constants';
import { handleFlag as ctfHandleFlag, showWin as ctfShowWin } from './Ctf';
import { reportErrorEvent } from './ErrorReport';
import {
  isReloading as gamesIsReloading,
  prep as gamesPrep,
  spectate as gamesSpectate,
  switchGame as gamesSwitchGame,
} from './Games';
import { viewport } from './Graphics';
import { disableCruiseMode as inputDisableCruiseMode } from './Input';
import { matchTimerHandleWhisper } from './MatchTimer';
import { add as mobAdd, despawn as mobDespawn, destroy as mobDestroy, network as mobNetwork } from './Mobs';
import type { MainToWorker, WorkerToMain } from './network-worker/protocol';
import { spiritShockwave as particlesSpiritShockwave } from './particles/Particles';
import {
  add as playerAdd,
  amIAlive as playersAmIAlive,
  boost as playerBoost,
  bounce as playerBounce,
  changeFlag as playerChangeFlag,
  changeType as playerChangeType,
  chat as playerChat,
  destroy as playerDestroy,
  fire as playerFire,
  impact as playerImpact,
  kill as playerKill,
  leaveHorizon as playerLeaveHorizon,
  myFlag as playersMyFlag,
  networkUpdate as playerNetworkUpdate,
  powerup as playerPowerup,
  respawn as playerRespawn,
  reteam as playerReteam,
  say as playerSay,
  stealth as playerStealth,
  teamChat as playerTeamChat,
  updateBadges as playerUpdateBadges,
  updateLevel as playerUpdateLevel,
  votemutePass as playerVotemutePass,
  whisper as playerWhisper,
} from './Players';
import { GAME_STATE_CODES, parseServerConfig, requireTarget, session } from './Session';
import { notePacket as hitchNotePacket } from './HitchLog';
import { auth } from './Settings';
import { effectRepel as soundEffectRepel, missileLaunch as soundMissileLaunch } from './Sound';
import type { KeyName } from './types/Input';
import {
  chatMuted as uiChatMuted,
  connectionLost as uiConnectionLost,
  errorHandler as uiErrorHandler,
  loggedIn as uiLoggedIn,
  newScore as uiNewScore,
  noteFlagGate as uiNoteFlagGate,
  scoreboardUpdate as uiScoreboardUpdate,
  serverMessage as uiServerMessage,
  setGameNotification as uiSetGameNotification,
  showCommandReply as uiShowCommandReply,
  updateGameInfo as uiUpdateGameInfo,
  updateScoreboardUIState as uiUpdateScoreboardUIState,
  updateUpgrades as uiUpdateUpgrades,
} from './UI';
import Vector from './Vector';

const WEBSOCKET_ERROR_MESSAGE_DURATION_MS = 30000;
const RECONNECT_MESSAGE_DURATION_MS = 600000;

/** When the server last spoke and how far its clock drifts, plus the round trip it last measured. */
export interface NetworkTiming {
  /** The round trip the server last reported, in ms. */
  ping: number;
  /** performance.now() of the last packet received. */
  timeNetwork: number;
  /** The last timed packet's drift against the server clock, in ms, smoothed. */
  jitter: number;
}
/** The router and the ping reply write here; readers, this module's included, use the view below. */
const timingState: NetworkTiming = { ping: 0, timeNetwork: 0, jitter: 0 };
export const timing: Readonly<NetworkTiming> = timingState;

// Baseline server packet clock (msg.clock / 100) for jitter calculation
let baseDivClk = -1;
let baseDivClkLastResetTime = -1;

// EWMA jitter smoothing: 0.8 * old + delta / 5
let jitterMemory = 0;

// Tombstones of recently seen messages for deduplication (numeric key -> timestamp)
const messageTombstoneMap = new Map<number, number>();
let lastTombstoneGcTime = 0;

let sendKeyCount = 0;

// Suppresses the reconnect prompt when the disconnect followed an explicit server error
let lastReceivedError: SERVER_ERRORS | null = null;

// ---- Network worker bridge -------------------------------------------------
//
// The wire layer (sockets, the protocol's encode/decode and the 50ms ACK loop)
// lives on a dedicated worker thread so the ACK keepalive survives
// tab-backgrounding and decode no longer competes with rendering.
//
// Timing/jitter bookkeeping stays HERE: `timing.timeNetwork` and `timing.jitter`
// are stamped/computed in dispatchIncomingMessage using the main thread's
// `performance.now()`, and are consumed throughout interpolation and entity
// timeouts — all on the main clock. The worker has a different
// `performance.now()` origin and must not own these.

const networkWorker = new Worker(new URL('./network-worker/worker.ts', import.meta.url), {
  type: 'module',
  name: 'network-worker',
});

networkWorker.addEventListener('message', (event: MessageEvent<WorkerToMain>) => {
  handleWorkerMessage(event.data);
});

// The socket handlers ran on the main thread in legacy, so their throws reached
// window.onerror and the backend; a throw inside the worker lands here instead.
networkWorker.onerror = (event: ErrorEvent) => {
  console.error('Network worker error:', event.message);
  reportErrorEvent(event);
};

function postToWorker(msg: MainToWorker) {
  networkWorker.postMessage(msg);
}

/** Posts an outbound packet to the worker, which also sends a KEY on the backup socket. */
function postSend(msg: ClientMessage) {
  postToWorker({ kind: 'send', msg });
}

/** Routes decoded packets and wire events from the worker into game state. A handler that
 *  throws reaches window.onerror, as it did from the socket's own message handler. */
function handleWorkerMessage(msg: WorkerToMain) {
  switch (msg.kind) {
    case 'message':
      dispatchIncomingMessage(msg.msg);
      break;

    case 'close':
      onPrimaryClose();
      break;

    case 'error':
      onSocketError();
      break;

    default: {
      const exhaustive: never = msg;
      throw new Error(`Network: unhandled worker message ${String(exhaustive)}`);
    }
  }
}

/** Sends a key press/release to the server, also handles spectator navigation. */
export function sendKey(keyCode: KeyName, isPressed: boolean) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    sendKeyCount++;
    const msg: ClientKeyMessage = {
      c: CLIENT_PACKETS.KEY,
      seq: sendKeyCount,
      key: KEY_CODES[keyCode],
      state: isPressed,
    };

    if (session.spectatingID != null && isPressed) {
      if (keyCode === 'RIGHT') {
        spectatePrev();
      } else if (keyCode === 'LEFT') {
        spectateNext();
      }
    }

    postSend(msg);
  }
}

export function sendChat(text: string) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientChatMessage = {
      c: CLIENT_PACKETS.CHAT,
      text: text,
    };
    postSend(msg);
  }
}

export function sendWhisper(playerId: number, text: string) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientWhisperMessage = {
      c: CLIENT_PACKETS.WHISPER,
      id: playerId,
      text: text,
    };
    postSend(msg);
  }
}

export function sendSay(text: string) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientSayMessage = {
      c: CLIENT_PACKETS.SAY,
      text: text,
    };
    postSend(msg);
  }
}

export function sendTeam(text: string) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientTeamChatMessage = {
      c: CLIENT_PACKETS.TEAMCHAT,
      text: text,
    };
    postSend(msg);
  }
}

export function sendCommand(com: string, data: string) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientCommandMessage = {
      c: CLIENT_PACKETS.COMMAND,
      com,
      data,
    };
    postSend(msg);
  }
}

export function voteMute(playerId: number) {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientVoteMuteMessage = {
      c: CLIENT_PACKETS.VOTEMUTE,
      id: playerId,
    };
    postSend(msg);
  }
}

export function getScores() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientScoreDetailedMessage = {
      c: CLIENT_PACKETS.SCOREDETAILED,
    };
    postSend(msg);
  }
}

/** Notifies the server of the player's visible area so it can cull off-screen updates. */
export function resizeHorizon() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    const msg: ClientHorizonMessage = {
      c: CLIENT_PACKETS.HORIZON,
      horizonX: Math.ceil(viewport.halfWidth / viewport.scale),
      horizonY: Math.ceil(viewport.halfHeight / viewport.scale),
    };
    postSend(msg);
  }
}

/** Closes all connections and resets network state. */
export function shutdown() {
  // Worker tears down sockets, clears the ACK loop, and resets its own state.
  postToWorker({ kind: 'disconnect' });

  // Reset the main-side timing/dispatch state.
  baseDivClk = -1;
  baseDivClkLastResetTime = -1;
  jitterMemory = 0;
  messageTombstoneMap.clear();
  lastTombstoneGcTime = 0;
  sendKeyCount = 0;
  lastReceivedError = null;
}

/** Remembers a server error so the close that follows it does not offer a reconnect. */
export function receivedError(errorCode: SERVER_ERRORS) {
  lastReceivedError = errorCode;
}

// Counterintuitively mapped to LEFT arrow key (inherited from original game)
export function spectateNext() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    sendCommand('spectate', '-1');
  }
}

// Counterintuitively mapped to RIGHT arrow key (inherited from original game)
export function spectatePrev() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    sendCommand('spectate', '-2');
  }
}

export function spectateForce() {
  if (session.state === GAME_STATE_CODES.PLAYING) {
    if (playersAmIAlive()) {
      sendCommand('spectate', '-3');
    } else {
      spectateNext();
    }
  }
}

function calculateJitterForOtherMessage(curPacketDivClk: number) {
  if (baseDivClk !== -1) {
    resetJitterMemoryIfDrifted(curPacketDivClk);
    timingState.jitter = curPacketDivClk - baseDivClk - (timing.timeNetwork - baseDivClkLastResetTime) - jitterMemory;
  }
}

/** EWMA jitter: 80% history + 20% current sample, clamped to ±100ms. */
function calculateJitterForPlayerMessage(curPacketDivClk: number) {
  timingState.jitter = 0;
  if (baseDivClk !== -1) {
    resetJitterMemoryIfDrifted(curPacketDivClk);
    const curTime = timing.timeNetwork;
    const delta = curPacketDivClk - baseDivClk - (curTime - baseDivClkLastResetTime);
    const newJitter = delta - (jitterMemory = 0.8 * jitterMemory + delta / 5);
    if (Math.abs(newJitter) < 100) {
      timingState.jitter = newJitter;
    }
  }
}

/** First message after connecting — initializes game state. The worker starts
 *  the ACK loop and opens the backup socket when it decodes this same packet. */
function handleLoginMessage(msg: ServerLoginMessage) {
  session.myID = msg.id;
  session.myTeam = msg.team;
  session.state = GAME_STATE_CODES.PLAYING;
  // Stored raw: the legacy client escaped here because the room name went into
  // innerHTML, but the scoreboard header is a Lit text node now, so escaping
  // would put the entity itself on screen (a room named "A/B" showed "A&#x2F;B").
  session.roomName = msg.room;
  session.gameType = msg.type;
  session.serverConfig = parseServerConfig(msg.serverConfiguration);
  gamesPrep();
  baseDivClk = msg.clock / 100;
  baseDivClkLastResetTime = performance.now();
}

function handlePingMessage(num: number) {
  const pongMessage: ClientPongMessage = {
    c: CLIENT_PACKETS.PONG,
    num,
  };
  postSend(pongMessage);
}

/** Elapsed time since the frame clock's tick in 60 fps frame units, with jitter compensation. */
export function reducedFactor() {
  const JITTER_THRESHOLD = 0.1;

  // Calculate elapsed time in frame units (60 FPS baseline)
  let elapsedFrames = (performance.now() - clock.time) / MS_PER_FRAME_60FPS;

  // Apply jitter compensation if jitter is significant
  if (Math.abs(timing.jitter) > JITTER_THRESHOLD) {
    elapsedFrames += timing.jitter / MS_PER_FRAME_60FPS;
  }

  return elapsedFrames;
}

// Reset jitter baseline after 1 hour of clock drift to prevent error accumulation
const JITTER_RESET_THRESHOLD_MS = 36e5;

function resetJitterMemoryIfDrifted(packetClockDivided: number) {
  if (Math.abs(packetClockDivided - baseDivClk) > JITTER_RESET_THRESHOLD_MS) {
    baseDivClk = packetClockDivided;
    baseDivClkLastResetTime = performance.now();
    jitterMemory = 0;
  }
}

/** The packets the server mirrors to the backup socket for the local player; with PING they carry its jitter sample. */
const PLAYER_SELF_PACKET_SET: ReadonlySet<ServerPacketCode> = new Set([
  SERVER_PACKETS.PLAYER_UPDATE,
  SERVER_PACKETS.PLAYER_FIRE,
  SERVER_PACKETS.EVENT_BOOST,
  SERVER_PACKETS.EVENT_BOUNCE,
]);

/** Whether the packet times the local player: one of the mirrored four about them, or a PING. */
function isPlayerMessage(msg: ServerMessage) {
  return msg.c === SERVER_PACKETS.PING || (PLAYER_SELF_PACKET_SET.has(msg.c) && 'id' in msg && msg.id === session.myID);
}

const _launchPos = new Vector();

/** PLAYER_FIRE: spawns the projectiles as mobs and plays the launch sound at the first one. */
function handlePlayerFireMessage(msg: ServerPlayerFireMessage) {
  for (const projectile of msg.projectiles) {
    mobAdd(projectile, false, msg.id);
  }
  if (msg.projectiles.length > 0) {
    const projectile = msg.projectiles[0]!;
    _launchPos.set(projectile.posX, projectile.posY);
    soundMissileLaunch(_launchPos, projectile.type);
  }
}

const _repelPos = new Vector();

/** EVENT_REPEL: moves the Goliath and everything it threw, then the shockwave effect and sound. */
function handleEventRepelMessage(msg: ServerEventRepelMessage) {
  playerNetworkUpdate(msg);
  for (const player of msg.players) {
    playerNetworkUpdate(player);
  }
  for (const mob of msg.mobs) {
    mobNetwork(mob, msg.id);
  }
  _repelPos.set(msg.posX, msg.posY);
  particlesSpiritShockwave(_repelPos);
  soundEffectRepel(_repelPos);
}

function handleLoginAndPlayers(msg: ServerLoginMessage) {
  handleLoginMessage(msg);
  uiLoggedIn();
  const botIds = new Set(msg.bots?.map((b) => b.id));
  for (const player of msg.players) {
    playerAdd(player, botIds.has(player.id), true);
  }
  // The flag went as the closest one the server has when it lacks the choice: say so, as /flag does
  uiNoteFlagGate(playersMyFlag);
}

function handlePlayerHitMessage(msg: ServerPlayerHitMessage) {
  playerImpact(msg);
  if (msg.type !== MOB_TYPES.FIREWALL) {
    mobDestroy(msg);
  }
}

function handleEventLeaveHorizonMessage(msg: ServerEventLeaveHorizonMessage) {
  if (msg.type === LEAVE_HORIZON_TYPES.PLAYER) {
    playerLeaveHorizon(msg);
  } else {
    mobDestroy(msg);
  }
}

/** Central message router: stamps the packet clock, updates jitter, drops a backup-socket duplicate and dispatches by packet type. */
function dispatchIncomingMessage(msg: ServerMessage) {
  // Only process when playing, or for LOGIN/ERROR (parity with the original)
  if (session.state !== GAME_STATE_CODES.PLAYING && msg.c !== SERVER_PACKETS.LOGIN && msg.c !== SERVER_PACKETS.ERROR) {
    return;
  }

  hitchNotePacket(msg.c);

  // --- Timing / jitter bookkeeping -----------------------------------------
  // The server mirrors the local player's own UPDATE, FIRE, BOOST and BOUNCE to the
  // backup socket, so the same packet can arrive twice; the tombstone drops the second.
  const forMe = isPlayerMessage(msg);
  if (forMe && msg.c !== SERVER_PACKETS.PING && 'clock' in msg && shouldDiscardTimestampedMessage(msg)) {
    return;
  }
  timingState.timeNetwork = performance.now();
  if ('clock' in msg) {
    if (forMe) {
      calculateJitterForPlayerMessage(msg.clock / 100);
    } else {
      calculateJitterForOtherMessage(msg.clock / 100);
    }
  }

  switch (msg.c) {
    case SERVER_PACKETS.PLAYER_UPDATE:
      playerNetworkUpdate(msg);
      break;

    case SERVER_PACKETS.PLAYER_RESPAWN:
      if (msg.id === session.myID) {
        inputDisableCruiseMode();
      }
      playerRespawn(msg);
      break;

    case SERVER_PACKETS.PLAYER_FLAG:
      playerChangeFlag(msg);
      break;

    case SERVER_PACKETS.EVENT_BOOST:
      playerBoost(msg);
      break;

    case SERVER_PACKETS.EVENT_BOUNCE:
      playerBounce(msg);
      break;

    case SERVER_PACKETS.CHAT_SAY:
      playerSay(msg);
      break;

    case SERVER_PACKETS.PLAYER_FIRE:
      playerFire(msg);
      handlePlayerFireMessage(msg);
      break;

    case SERVER_PACKETS.PLAYER_NEW:
      playerAdd(msg, msg.isBot);
      break;

    case SERVER_PACKETS.PLAYER_LEAVE:
      playerDestroy(msg.id);
      break;

    case SERVER_PACKETS.PLAYER_TYPE:
      playerChangeType(msg);
      break;

    case SERVER_PACKETS.PLAYER_HIT:
      handlePlayerHitMessage(msg);
      break;

    case SERVER_PACKETS.PLAYER_KILL:
      if (msg.id === session.myID) {
        inputDisableCruiseMode();
      }
      playerKill(msg);
      break;

    case SERVER_PACKETS.PLAYER_UPGRADE:
      uiUpdateUpgrades([msg.speed, msg.defense, msg.energy, msg.missile], msg.upgrades, msg.type);
      break;

    case SERVER_PACKETS.PLAYER_POWERUP:
      playerPowerup(msg);
      break;

    case SERVER_PACKETS.PLAYER_LEVEL:
      playerUpdateLevel(msg);
      break;

    case SERVER_PACKETS.PLAYER_RETEAM:
      for (const player of msg.players) {
        playerReteam(player);
      }
      break;

    case SERVER_PACKETS.LOGIN:
      handleLoginAndPlayers(msg);
      break;

    case SERVER_PACKETS.ERROR:
      uiErrorHandler(msg);
      break;

    case SERVER_PACKETS.EVENT_REPEL:
      handleEventRepelMessage(msg);
      break;

    case SERVER_PACKETS.EVENT_LEAVEHORIZON:
      handleEventLeaveHorizonMessage(msg);
      break;

    case SERVER_PACKETS.EVENT_STEALTH:
      playerStealth(msg);
      break;

    case SERVER_PACKETS.GAME_FLAG:
      ctfHandleFlag(msg);
      break;

    case SERVER_PACKETS.GAME_PLAYERSALIVE:
      btrPlayersAlive(msg.players);
      break;

    case SERVER_PACKETS.GAME_SPECTATE:
      gamesSpectate(msg.id);
      break;

    case SERVER_PACKETS.GAME_FIREWALL:
      btrHandleFirewall(msg);
      break;

    case SERVER_PACKETS.MOB_UPDATE:
      mobNetwork(msg, msg.ownerId);
      break;

    case SERVER_PACKETS.MOB_UPDATE_STATIONARY:
      mobNetwork(msg);
      break;

    case SERVER_PACKETS.MOB_DESPAWN:
      mobDespawn(msg);
      break;

    case SERVER_PACKETS.MOB_DESPAWN_COORDS:
      mobDestroy(msg);
      break;

    case SERVER_PACKETS.SCORE_UPDATE:
      uiNewScore(msg);
      break;

    case SERVER_PACKETS.SCORE_BOARD:
      uiScoreboardUpdate(msg.data, msg.rankings);
      playerUpdateBadges(msg.data);
      break;

    case SERVER_PACKETS.SCORE_DETAILED:
    case SERVER_PACKETS.SCORE_DETAILED_CTF:
    case SERVER_PACKETS.SCORE_DETAILED_BTR:
      uiUpdateScoreboardUIState(msg);
      break;

    case SERVER_PACKETS.PING:
      handlePingMessage(msg.num);
      break;

    case SERVER_PACKETS.PING_RESULT:
      timingState.ping = msg.ping;
      uiUpdateGameInfo();
      break;

    // Chat receive is no longer mobile-gated: mobile now has a chat UI, so
    // incoming public/team/whisper messages (and vote-mute results) populate
    // uiState.chat.messages the same as desktop. (SAY/proximity was never gated
    // — it renders as speech bubbles.) The original hard-disabled all of this on
    // mobile because there was no surface to show it.
    case SERVER_PACKETS.CHAT_PUBLIC:
      playerChat(msg);
      break;

    case SERVER_PACKETS.CHAT_TEAM:
      playerTeamChat(msg);
      break;

    case SERVER_PACKETS.CHAT_WHISPER:
      if (!matchTimerHandleWhisper(msg)) {
        playerWhisper(msg);
      }
      break;

    case SERVER_PACKETS.CHAT_VOTEMUTEPASSED:
      playerVotemutePass(msg);
      break;

    case SERVER_PACKETS.CHAT_VOTEMUTED:
      uiChatMuted();
      break;

    case SERVER_PACKETS.SERVER_MESSAGE:
      uiServerMessage(msg);
      break;

    case SERVER_PACKETS.SERVER_CUSTOM:
      handleCustomMessage(msg);
      break;

    case SERVER_PACKETS.COMMAND_REPLY:
      uiShowCommandReply(msg);
      break;

    default:
      // BACKUP, ACK and anything newer than this client: ignored, as the original did
      break;
  }
}

/** SERVER_CUSTOM carries JSON: a BTR or CTF result, or a suggestion to switch game. */
function handleCustomMessage(msg: ServerServerCustomMessage) {
  let parsedData: unknown;
  try {
    parsedData = JSON.parse(msg.data);
  } catch {
    return;
  }
  switch (msg.type) {
    case SERVER_CUSTOM_TYPES.BTR:
      btrShowWin(parsedData as ServerCustomBTRData);
      break;
    case SERVER_CUSTOM_TYPES.CTF:
      ctfShowWin(parsedData as ServerCustomCTFData);
      break;
    case SERVER_CUSTOM_TYPES.SWITCH_GAME_SUGGESTION:
      showSwitchGameSuggestion(parsedData as ServerCustomSwitchGameData);
      break;
  }
}

const GAME_SERVER_ID_RE = /^[a-z0-9]+-[a-z0-9]+$/;

function isValidGameServerId(gameServerId: string) {
  return GAME_SERVER_ID_RE.test(gameServerId);
}

function showSwitchGameSuggestion(data: ServerCustomSwitchGameData) {
  if (data && data.message && data.button && isValidGameServerId(data.server)) {
    uiSetGameNotification({
      type: 'alert',
      title: '',
      message: data.message,
      showReconnect: false,
      timeoutMs: 5000,
      switchGameButton: { serverId: data.server, label: data.button },
    });
  }
}

/** Returns true if this message was already seen (duplicate from dual sockets). */
function shouldDiscardTimestampedMessage(msg: { c: number; clock: number }) {
  // Numeric key: packet type + clock * 256. Collision-free since c < 256 and clock is uint32.
  const msgTombstone = msg.c + msg.clock * 256;
  const now = performance.now();

  // GC: every 15s, remove entries older than 30s
  if (now - lastTombstoneGcTime > 15e3) {
    for (const [key, timestamp] of messageTombstoneMap) {
      if (now - timestamp > 3e4) {
        messageTombstoneMap.delete(key);
      }
    }
    lastTombstoneGcTime = now;
  }

  if (messageTombstoneMap.has(msgTombstone)) {
    return true;
  } else {
    messageTombstoneMap.set(msgTombstone, now);
    return false;
  }
}

export function reconnectMessage() {
  if (!gamesIsReloading()) {
    uiSetGameNotification({
      type: 'alert',
      title: 'DISCONNECTED',
      message: 'Connection reset',
      showReconnect: true,
      timeoutMs: RECONNECT_MESSAGE_DURATION_MS,
    });
  }
}

export function reconnect() {
  uiState.gameNotification = null;
  gamesSwitchGame();
}

/** Main-thread half of the old primary-socket close (the worker stops its own
 *  ACK loop). Only fires for unexpected closes — intentional disconnects are
 *  silent — and the `CONNECTING` guard neutralizes the reconnect-during-switch race.
 *  UI.connectionLost clears the screen for the disconnect notice. */
function onPrimaryClose() {
  if (session.state !== GAME_STATE_CODES.CONNECTING) {
    session.serverConfig = null;
    session.state = GAME_STATE_CODES.CONNECTING;
    uiConnectionLost();
    if (lastReceivedError === null) {
      reconnectMessage();
    }
  }
}

function onSocketError() {
  // The worker logs the underlying Event (it owns the socket); we surface the
  // UI message. WebSocket `error` events are intentionally opaque in browsers
  // (security — don't leak network topology), so we can only list common
  // culprits rather than pinpoint one.
  uiServerMessage({
    type: SERVER_MESSAGE_TYPES.ALERT,
    text: "Couldn't reach the game server. Common causes: VPN, content blockers, or network restrictions.",
    duration: WEBSOCKET_ERROR_MESSAGE_DURATION_MS,
  });
}

/** Opens the connection: builds the LOGIN packet from main-thread state and
 *  hands the worker the socket URL + login to send when the primary opens. */
export function setup() {
  const target = requireTarget();
  const url = new URL(target.kind === 'custom' ? target.url : `wss://${target.host}/${target.path}`);
  // The extension handshake: other servers ignore the query, and ours answer with `ext` in the LOGIN configuration
  url.searchParams.set('ext', String(EXTENSION_LEVEL));

  const gameToken = auth.tokens?.game;
  const sessionToken = gameToken ? JSON.stringify({ token: gameToken }) : 'none';

  // The placeholder goes as is: it fails the server's check, which then picks the flag of the player's address
  const myFlag = playersMyFlag;
  const login: ClientLoginMessage = {
    c: CLIENT_PACKETS.LOGIN,
    protocol: PROTOCOL_VERSION,
    name: session.myName,
    session: sessionToken,
    horizonX: Math.ceil(viewport.halfWidth / viewport.scale),
    horizonY: Math.ceil(viewport.halfHeight / viewport.scale),
    flag: (myFlag === UNKNOWN_FLAG ? myFlag : toServerFlag(myFlag)).toUpperCase(),
  };

  postToWorker({ kind: 'connect', url: url.href, login });
}
